/**
 * Sponsorship slot coverage — sample inventory model (front-end demo only).
 *
 * Each market offers a fixed number of slots per package tier. The "taken"
 * count is DERIVED from the existing SPONSOR_ORDERS ledger so coverage always
 * reconciles with the rest of the revenue demo. Nothing here is persisted.
 */

import {
  REGIONS,
  packageById,
  SPONSOR_ORDERS,
} from '@/pages/demo/revenue/components/revenueData';
import type { Region } from '@/pages/demo/revenue/components/revenueData';

/** Package tiers that carry per-region inventory. */
export const COVERAGE_PACKAGE_IDS = ['local', 'featured', 'regional', 'presenting'];

/**
 * Sample capacity — how many slots each market can sell per package tier.
 * Larger markets carry a little more room; the presenting tier is capped at one
 * per market because it is a platform-level partnership.
 */
export const CAPACITY: Record<string, Record<string, number>> = {
  'los-angeles': { local: 3, featured: 2, regional: 2, presenting: 1 },
  'orange-county': { local: 3, featured: 2, regional: 1, presenting: 1 },
  'inland-empire': { local: 2, featured: 2, regional: 2, presenting: 1 },
  'san-diego': { local: 3, featured: 2, regional: 2, presenting: 1 },
  phoenix: { local: 3, featured: 1, regional: 2, presenting: 1 },
  tucson: { local: 2, featured: 2, regional: 1, presenting: 1 },
};

export type PackageCoverage = {
  packageId: string;
  name: string;
  capacity: number;
  taken: number;
  filled: number;
  open: number;
  soldOut: boolean;
};

export type RegionCoverage = {
  region: Region;
  packages: PackageCoverage[];
  capacity: number;
  filled: number;
  open: number;
  soldOutPackages: number;
};

export const COVERAGE: RegionCoverage[] = REGIONS.map((region) => {
  const caps = CAPACITY[region.id] ?? {};
  const packages: PackageCoverage[] = COVERAGE_PACKAGE_IDS.map((packageId) => {
    const capacity = caps[packageId] ?? 0;
    const taken = SPONSOR_ORDERS.filter(
      (o) => o.regionId === region.id && o.packageId === packageId,
    ).length;
    const filled = Math.min(taken, capacity);
    const open = Math.max(capacity - filled, 0);
    return {
      packageId,
      name: packageById(packageId)?.name ?? packageId,
      capacity,
      taken,
      filled,
      open,
      soldOut: open === 0,
    };
  });

  const capacity = packages.reduce((s, p) => s + p.capacity, 0);
  const filled = packages.reduce((s, p) => s + p.filled, 0);
  const open = packages.reduce((s, p) => s + p.open, 0);
  const soldOutPackages = packages.filter((p) => p.soldOut).length;
  return { region, packages, capacity, filled, open, soldOutPackages };
});

export const COVERAGE_TOTALS = {
  capacity: COVERAGE.reduce((s, r) => s + r.capacity, 0),
  filled: COVERAGE.reduce((s, r) => s + r.filled, 0),
  open: COVERAGE.reduce((s, r) => s + r.open, 0),
  soldOutMarkets: COVERAGE.filter((r) => r.open === 0).length,
  openMarkets: COVERAGE.filter((r) => r.open > 0).length,
};

export function soldPercent(coverage: RegionCoverage): number {
  if (!coverage.capacity) return 0;
  return Math.round((coverage.filled / coverage.capacity) * 100);
}

/** Regions ranked by the number of open slots — the best prospecting targets first. */
export function regionsByOpenSlots(list: RegionCoverage[]): RegionCoverage[] {
  return [...list].sort((a, b) => b.open - a.open || a.region.city.localeCompare(b.region.city));
}